/**
 * @fileoverview Composes Location section state, validation, and field handlers.
 */

import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import {
  getCompanyFieldValue,
  isFieldReadOnlyForProfile,
} from "../../../utils/common";
import { useFieldValidation } from "../../../hooks/useFieldValidation";
import { useLocationReferenceData } from "./useLocationReferenceData";
import { useSecondAddressState } from "./useSecondAddressState";

/**
 * @param {Object} props
 * @param {Object} props.company - Company data object containing address fields
 * @param {Function} props.onFieldChange - Callback when a field value changes
 * @param {Function} props.onValidationChange - Callback when validation state changes
 * @param {number} [props.selectedProfile=0] - Selected company profile index
 */
export function useLocationSection({
  company,
  onFieldChange,
  onValidationChange,
  selectedProfile = 0,
}) {
  const navigate = useNavigate();
  const prevHasErrorsRef = useRef(false);
  const referenceData = useLocationReferenceData(navigate, company);
  const addressState = useSecondAddressState(company);

  const {
    handleFieldChange,
    getErrorMessage,
    getFieldValidationState,
    hasErrors,
  } = useFieldValidation({
    domicilio1Calle: "",
    domicilio1NumeroExterior: "",
    domicilio1NumeroInterior: "",
    domicilio1EntreCalle1: "",
    domicilio1EntreCalle2: "",
    domicilio2Calle: "",
    domicilio2NumeroExterior: "",
    domicilio2NumeroInterior: "",
    domicilio2EntreCalle1: "",
    domicilio2EntreCalle2: "",
    sucursales: getCompanyFieldValue(
      company,
      "sucursales",
      selectedProfile,
      "",
    ),
  });

  // Notify parent of validation changes only when the state actually changes
  useEffect(() => {
    if (onValidationChange && prevHasErrorsRef.current !== hasErrors) {
      prevHasErrorsRef.current = hasErrors;
      onValidationChange(hasErrors);
    }
  }, [hasErrors, onValidationChange]);

  const handleInputChange = (field, value) => {
    if (onFieldChange) {
      onFieldChange(field, value);
    }
  };

  // handleToggleChange uses the same implementation as handleInputChange
  const handleToggleChange = handleInputChange;

  // Check if direccion fields should be read-only for this profile
  const isAddressReadOnly = isFieldReadOnlyForProfile(
    "direccion",
    selectedProfile,
  );

  return {
    ...referenceData,
    ...addressState,
    handleFieldChange,
    getErrorMessage,
    getFieldValidationState,
    handleInputChange,
    handleToggleChange,
    isAddressReadOnly,
  };
}
